import { useState, useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import {
  analyticsService,
  AnalyticsData,
  TriggerResponse,
} from 'services/analyticsService';

export interface AnalyticsState {
  currentMonthData: AnalyticsData | null;
  latestData: AnalyticsData | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

export const useAnalytics = () => {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [currentMonthData, setCurrentMonthData] =
    useState<AnalyticsData | null>(null);

  const fetchCurrentMonthAnalytics = useCallback(async () => {
    if (!token) {
      setError('No authentication token available');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await analyticsService.getAllTimeAnalytics(token);

      if (response) {
        setCurrentMonthData(response);
        setLastUpdated(new Date());
        setIsLoading(false);
      } else {
        setError('No analytics data available for current month');
      }
    } catch (error: any) {
      console.error('Error fetching current month analytics:', error);
      setError(
        error.response?.data?.message || 'Failed to fetch analytics data',
      );
      setIsLoading(false);
    }
  }, [token, setError]);

  const triggerAnalytics =
    useCallback(async (): Promise<TriggerResponse | null> => {
      if (!token) {
        setError('No authentication token available');
        return null;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await analyticsService.triggerAnalytics(token);

        if (response.success) {
          // Refresh the data after successful trigger
          await fetchCurrentMonthAnalytics();
          return response;
        } else {
          setError(response.message || 'Failed to trigger analytics');
          return response;
        }
      } catch (error: any) {
        console.error('Error triggering analytics:', error);
        const errorMessage =
          error.response?.data?.message || 'Failed to trigger analytics';
        setError(errorMessage);
        return null;
      }
    }, [token, setError, fetchCurrentMonthAnalytics]);

  const formatCurrency = useCallback(
    (amount: number | null | undefined): string => {
      if (amount == null || isNaN(amount)) {
        return '$0';
      }

      if (amount >= 1000000) {
        return `$${(amount / 1000000).toFixed(1)}M`;
      } else if (amount >= 1000) {
        return `$${(amount / 1000).toFixed(0)}K`;
      } else {
        return `$${amount.toFixed(0)}`;
      }
    },
    [],
  );

  const formatPercentage = useCallback(
    (current: number, previous: number): string => {
      if (previous === 0) return current > 0 ? '+100%' : '0%';
      const percentage = ((current - previous) / previous) * 100;
      return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(0)}%`;
    },
    [],
  );

  return {
    currentMonthData,
    isLoading,
    error,
    lastUpdated,
    fetchCurrentMonthAnalytics,
    triggerAnalytics,
    formatCurrency,
    formatPercentage,
  };
};
